import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import format from "date-fns/format";
import { type FunctionComponent, useEffect, useState } from "react";
import type { Range, RangeKeyDict } from "react-date-range";

import type { ArticlesSearchFilterByDateRange } from "scmp-app/components/search/contexts";

import type { TimeRangePreset } from "./helpers";
import {
  formatDateRangeText,
  getTimeRangeForDays,
  TimeRangePresets,
  toHongKongTimestamp,
} from "./helpers";
import CalendarIcon from "./icon-calendar.svg";
import ClearIcon from "./icon-clear.svg";
import {
  ActionButton,
  Actions,
  Box,
  CalendarPanel,
  ClearButton,
  FieldGroup,
  FieldLabel,
  MonthNav,
  MonthNavButton,
  MonthNavLabel,
  PresetButtonActive,
  PresetButtonInactive,
  PresetRow,
  RightActions,
  StyledDateRange,
  TriggerActive,
  TriggerIconActive,
  TriggerIconInactive,
  TriggerInactive,
  TriggerTextActive,
  TriggerTextInactive,
} from "./styles";

type ActivePreset = "custom" | null | TimeRangePreset;

type Props = {
  dateRange?: ArticlesSearchFilterByDateRange;
  dateRangeText: string;
  onChange: (dateRange: ArticlesSearchFilterByDateRange, text: string) => void;
};

const createDefaultRange = (): Range => ({
  endDate: new Date(),
  key: "selection",
  startDate: new Date(),
});

export const TimeRangeField: FunctionComponent<Props> = ({
  dateRange,
  dateRangeText,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [temporaryRange, setTemporaryRange] = useState<Range>(createDefaultRange);
  const [activePreset, setActivePreset] = useState<ActivePreset>(null);

  const hasValue = dateRangeText !== "" && dateRangeText !== "All";

  useEffect(() => {
    if (!isOpen) return;
    if (dateRange?.startDate && dateRange.endDate) {
      setTemporaryRange({
        endDate: new Date(dateRange.endDate),
        key: "selection",
        startDate: new Date(dateRange.startDate),
      });
      const matchedPreset = TimeRangePresets.find(preset => preset.label === dateRangeText);
      setActivePreset(matchedPreset ?? "custom");
    } else {
      setTemporaryRange(createDefaultRange());
      setActivePreset(null);
    }
  }, [dateRange?.endDate, dateRange?.startDate, dateRangeText, isOpen]);

  const handlePresetClick = (preset: TimeRangePreset) => {
    onChange(getTimeRangeForDays(preset.days), preset.label);
    setIsOpen(false);
  };

  const handleCalendarChange = (item: RangeKeyDict) => {
    const selection = item.selection;
    setTemporaryRange({ ...selection, key: "selection" });
    setActivePreset("custom");
  };

  const handleApply = () => {
    if (activePreset && activePreset !== "custom") {
      onChange(getTimeRangeForDays(activePreset.days), activePreset.label);
    } else if (temporaryRange.startDate && temporaryRange.endDate) {
      onChange(
        {
          endDate: toHongKongTimestamp(temporaryRange.endDate, "end"),
          startDate: toHongKongTimestamp(temporaryRange.startDate, "start"),
        },
        formatDateRangeText(temporaryRange.startDate, temporaryRange.endDate),
      );
    }
    setIsOpen(false);
  };

  const handleClearAll = () => {
    onChange({}, "All");
    setActivePreset(null);
    setTemporaryRange(createDefaultRange());
    setIsOpen(false);
  };

  const Trigger = hasValue ? TriggerActive : TriggerInactive;
  const TriggerIcon = hasValue ? TriggerIconActive : TriggerIconInactive;
  const TriggerText = hasValue ? TriggerTextActive : TriggerTextInactive;
  const CustomPresetButton = activePreset === "custom" ? PresetButtonActive : PresetButtonInactive;

  return (
    <FieldGroup>
      <FieldLabel>Time Range</FieldLabel>
      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        <Box>
          <Trigger
            aria-expanded={isOpen ? "true" : undefined}
            onClick={() => setIsOpen(current => !current)}
            type="button"
          >
            <TriggerIcon>
              <CalendarIcon />
            </TriggerIcon>
            <TriggerText>{hasValue ? dateRangeText : "Select date range"}</TriggerText>
            {hasValue && (
              <ClearButton
                aria-label="Clear"
                onClick={event => {
                  event.stopPropagation();
                  handleClearAll();
                }}
              >
                <ClearIcon />
              </ClearButton>
            )}
          </Trigger>

          {isOpen && (
            <CalendarPanel>
              <PresetRow>
                {TimeRangePresets.map(preset => {
                  const isSelected =
                    activePreset !== null &&
                    activePreset !== "custom" &&
                    activePreset.label === preset.label;
                  const PresetButton = isSelected ? PresetButtonActive : PresetButtonInactive;
                  return (
                    <PresetButton
                      key={preset.label}
                      onClick={() => handlePresetClick(preset)}
                      type="button"
                    >
                      {preset.label}
                    </PresetButton>
                  );
                })}
                <CustomPresetButton onClick={() => setActivePreset("custom")} type="button">
                  Custom
                </CustomPresetButton>
              </PresetRow>

              <StyledDateRange
                dateDisplayFormat="dd/MM/yyyy"
                editableDateInputs
                moveRangeOnFirstSelection={false}
                navigatorRenderer={(
                  focusedDate: Date,
                  changeShownDate: (
                    value: Date | number | string,
                    mode?: "monthOffset" | "set" | "setMonth" | "setYear",
                  ) => void,
                ) => (
                  <MonthNav>
                    <MonthNavButton
                      aria-label="Previous month"
                      onClick={() => changeShownDate(-1, "monthOffset")}
                      type="button"
                    >
                      <ArrowLeftIcon />
                    </MonthNavButton>
                    <MonthNavLabel>{format(focusedDate, "MMMM yyyy")}</MonthNavLabel>
                    <MonthNavButton
                      aria-label="Next month"
                      onClick={() => changeShownDate(1, "monthOffset")}
                      type="button"
                    >
                      <ArrowRightIcon />
                    </MonthNavButton>
                  </MonthNav>
                )}
                onChange={handleCalendarChange}
                rangeColors={["rgba(120, 138, 196, 0.25)"]}
                ranges={[temporaryRange]}
                weekdayDisplayFormat="EEE"
                weekStartsOn={1}
              />

              <Actions>
                <ActionButton onClick={handleClearAll} type="button">
                  Clear All
                </ActionButton>
                <RightActions>
                  <ActionButton onClick={() => setIsOpen(false)} type="button">
                    Cancel
                  </ActionButton>
                  <ActionButton $primary onClick={handleApply} type="button">
                    Apply
                  </ActionButton>
                </RightActions>
              </Actions>
            </CalendarPanel>
          )}
        </Box>
      </ClickAwayListener>
    </FieldGroup>
  );
};

TimeRangeField.displayName = "TimeRangeField";
